import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useTranslations } from "use-intl";
import { z } from "zod";

import { usePromoCodeValidation } from "@/app/lib/hooks/usePromoCodeValidation";
import { getTravellerTypeAtTravelDate } from "@/app/lib/utils/ageValidation";
import { BannersPlacement } from "@/app/ui/banners-placement/BannersPlacement";
import BottomDrawer from "@/app/ui/flight-purchase/BottomDrawer";
import InstallmentDetails from "@/app/ui/flight-purchase/InstallmentDetails";
import { PromoCodeForm } from "@/app/ui/PromoCodeForm";
import { usePaymentContext } from "@/contexts/PaymentContext";
import { HotelAndRoomCard } from "@/features/hotels/components/hotel/HotelAndRoomCard";
import { MainTravellerRadioButtons } from "@/features/hotels/components/hotel/MainTravellerRadioButtons";
import { TermsAndConditions } from "@/features/hotels/components/hotel/TermsAndConditions";
import type { HotelBooking } from "@/features/hotels/types/types";
import type {
  FindHotelsOutputData,
  FindUserTravelersTravelers,
} from "@/types/codegen";
import { BannersPlacementSlug } from "@/types/enums";
import { includeBnplFee, calculateMonthlyInstallment } from "@/utils/bnpl-fee";
import { formatCurrency } from "@/utils/currency";
import { Feature, isFeatureEnabled } from "@/utils/featureFlags";

export interface SummaryFormValues {
  roomAssignments: Record<string, { travelers: string[] }>;
  leadTraveler: string;
  termsAndConditions: boolean;
}

export interface HotelBookingSummaryFormProps {
  booking: HotelBooking;
  travelDate: Date;
  promoCode: string | null;
  setPromoCode: (code: string | null) => void;
  hotel: FindHotelsOutputData;
  loading: boolean;
  disabledSubmit?: boolean;
  rooms?: { adults: number; children: number }[];
  buttonLabel: string;
  selectedTravellers: FindUserTravelersTravelers[];
  handleSubmit: (values: SummaryFormValues) => void;
}

export const HotelBookingSummaryForm = ({
  booking,
  travelDate,
  promoCode,
  setPromoCode,
  hotel,
  loading,
  rooms = [],
  buttonLabel,
  disabledSubmit,
  selectedTravellers,
  handleSubmit: onSubmit,
}: HotelBookingSummaryFormProps) => {
  const t = useTranslations();
  const { paymentType, selectedInstallments } = usePaymentContext();

  // TODO: what if price changes before the booking?
  // TODO: pass code to purchase
  const { data: promoCodeData, validationStatus: promoCodeValidationStatus } =
    usePromoCodeValidation({
      service: "hotels",
      validationRequestData: {
        code: promoCode || "",
        availabilityToken: booking.availabilityToken || "",
      },
    });

  const hotelTotalPrice =
    booking?.rooms.reduce((acc, room) => acc + (room.price?.value || 0), 0) ||
    0;

  const totalPrice =
    paymentType === "bnpl" ? includeBnplFee(hotelTotalPrice) : hotelTotalPrice;
  const totalPriceWithPromoCode = promoCodeData?.finalPrice || totalPrice;

  const validationSchema = z.object({
    termsAndConditions: z.boolean().refine((v) => v === true),
    leadTraveler: z.string().min(1),
    roomAssignments: z
      .object(
        Object.fromEntries(
          booking.rooms.map((room, index) => [
            room.roomToken,
            z.object({
              travelers: z
                .array(z.string())
                .length(rooms[index].adults + rooms[index].children),
            }),
          ]),
        ),
      )
      .required(),
  });

  const leadTraveler = selectedTravellers.find(
    (traveller) =>
      getTravellerTypeAtTravelDate(traveller.birthDate, travelDate) === "adult",
  );

  const methods = useForm<
    z.input<typeof validationSchema>,
    undefined,
    z.infer<typeof validationSchema>
  >({
    defaultValues: {
      roomAssignments:
        booking.rooms.length === 1
          ? {
              [booking.rooms[0].roomToken]: {
                travelers: selectedTravellers.map((traveller) => traveller.id),
              },
            }
          : {},
      leadTraveler: leadTraveler?.id || "",
      termsAndConditions: false,
    },
    resolver: zodResolver(validationSchema),
  });

  const {
    register,
    control,
    handleSubmit,
    formState: { isValid, isDirty },
  } = methods;

  return (
    <FormProvider {...methods}>
      <div className="flex w-full flex-col">
        <HotelAndRoomCard
          hotel={hotel}
          hotelBookingData={booking}
          rooms={rooms}
        />

        <div className="section-title mt-4">
          {t("travellers.mainTraveller")}
        </div>
        <div className="section-card p-0">
          <div className="p-4 pb-2 text-sm text-text-tertiary">
            {t("travellers.markMainTraveller")}
          </div>

          <Controller
            control={control}
            name="leadTraveler"
            render={({ field }) => (
              <MainTravellerRadioButtons
                {...field}
                travelDate={travelDate}
                selectedTravellers={selectedTravellers}
              />
            )}
          />
        </div>

        <div className="section-card mt-2">
          <div className="text-sm text-text-title">
            {t("hotels.bookingPolicy")}
          </div>
        </div>

        <BannersPlacement
          slug={BannersPlacementSlug.CHECKOUT}
          classNames={{
            wrapper: "-mx-4 mt-4",
          }}
        />

        {isFeatureEnabled(Feature.PromoCodes) ? (
          <div className="mt-4">
            <PromoCodeForm
              appliedCode={promoCode || undefined}
              validationStatus={promoCodeValidationStatus}
              onCodeApply={({ code }) => {
                setPromoCode(code);
              }}
              onCodeRemove={() => {
                setPromoCode(null);
              }}
            />
          </div>
        ) : null}

        <div className="section-title mt-4">{t("summary")}</div>
        <div className="section-card">
          <TermsAndConditions {...register("termsAndConditions")} />
        </div>

        {paymentType === "bnpl" ? (
          <InstallmentDetails
            promoCodeDiscount={promoCodeData?.discount}
            totalPrice={totalPriceWithPromoCode}
          />
        ) : (
          <div className="section-card mt-2 flex flex-col gap-2 text-sm">
            {promoCodeData?.discount ? (
              <div className="flex justify-between">
                <div className="text-text-tertiary">
                  {t("promoCode.promoCodeDiscount")}
                </div>
                <div className="font-bold text-core-accent-500">
                  {formatCurrency(promoCodeData?.discount)}
                </div>
              </div>
            ) : null}

            <div className="flex justify-between">
              <div className="text-text-tertiary">{t("total")}</div>
              <div className="font-bold text-text-primary">
                {formatCurrency(
                  totalPriceWithPromoCode,
                  booking?.price?.currency,
                )}
              </div>
            </div>

            <div className="flex justify-between">
              <div className="text-text-tertiary">{t("paymentMethod")}</div>
              <div className="font-bold text-text-primary">{t("qiCard")}</div>
            </div>
          </div>
        )}
      </div>

      <BottomDrawer
        loading={loading}
        totalPrice={
          paymentType === "bnpl"
            ? undefined
            : formatCurrency(totalPriceWithPromoCode)
        }
        payLaterPrice={
          paymentType === "bnpl"
            ? formatCurrency(
                calculateMonthlyInstallment(
                  totalPriceWithPromoCode,
                  selectedInstallments,
                ),
              )
            : undefined
        }
        buttonLabel={buttonLabel}
        onButtonClick={handleSubmit((data) => onSubmit(data))}
        disabled={
          disabledSubmit ||
          !isValid ||
          !isDirty ||
          promoCodeValidationStatus?.type === "loading"
        }
      />
    </FormProvider>
  );
};
